import {
  EquipmentAssignmentRequestDto,
  EquipmentDto,
  OperationalEquipmentDto,
  WorkAreaType,
} from '@operations/app/api'
import { EquipmentAssignmentUpsertedPayload } from '@operations/app/api/signalRDtos/equipmentAssignmentUpsertedPayload'
import { EquipmentLastJobUpsertedPayload } from '@operations/app/api/signalRDtos/equipmentLastJobUpsertedPayload'
import { IOperationalEquipmentFormData } from '@operations/features/operationalEquipment/models/operational-equipment-assignment.model'
import { EventTypes } from '@operations/messages/eventsTypes'
import equipmentService from '@operations/services/equipmentService'
import { IEvent, IMessageBus } from '@tom-ui/utils'
import { makeObservable, observable, runInAction } from 'mobx'
import { BaseEntityStore } from './base/BaseEntityStore'

export class OperationalEquipmentStore extends BaseEntityStore<OperationalEquipmentDto> {
  fecthedAll = false

  constructor(private readonly messageBus: IMessageBus) {
    super()

    makeObservable(this, {
      fecthedAll: observable,
    })

    this.messageBus.subscribeEvent<EquipmentLastJobUpsertedPayload>(
      EventTypes.EquipmentLastJobUpserted,
      this.handleLastJobUpsertedEvent,
    )

    this.messageBus.subscribeEvent<EquipmentAssignmentUpsertedPayload>(
      EventTypes.EquipmentAssignmentUpserted,
      this.handleAssignmentUpsertedEvent,
    )

    this.messageBus.subscribeEvent<EquipmentDto>(
      EventTypes.EquipmentUpserted,
      this.handleEquipmentUpsertedEvent,
    )
  }

  public async fetchData() {
    if (this.fecthedAll) return

    const moves = await equipmentService.getOperationalEquipments()

    this.updateStoreItems(moves)

    runInAction(() => {
      this.fecthedAll = true
    })
  }

  public async updateEquipmentsAssignment(formData: IOperationalEquipmentFormData) {
    const assignments = [
      ...(formData.vesselAssignments?.assignments ?? [])
        .filter(x => x?.workAreaId && x?.isDeleted !== true)
        .map(x => ({
          workAreaId: x.workAreaId!,
          bays: [],
        })),

      ...(formData.trainAssignments?.assignments ?? [])
        .filter(x => x?.workAreaId && x?.isDeleted !== true)
        .map(x => ({
          workAreaId: x.workAreaId!,
          bays: [],
        })),

      ...(formData.yardAssignments?.assignments ?? [])
        .filter(x => x?.workAreaId && x?.isDeleted !== true)
        .map(x => ({
          workAreaId: x.workAreaId!,
          bays: x.assignedBays ?? [],
        })),
    ].reduce((acc, x) => {
      const exists = acc.some(y => y.workAreaId === x.workAreaId)
      if (!exists) acc.push(x)

      return acc
    }, [] as EquipmentAssignmentRequestDto[])

    const workAreaTypesToRemove: WorkAreaType[] = []
    if (!formData.vesselAssignments?.visitIds.length) workAreaTypesToRemove.push(WorkAreaType.Crane)
    if (!formData.trainAssignments?.visitIds.length) workAreaTypesToRemove.push(WorkAreaType.Train)
    if (!formData.yardAssignments?.workAreaIds.length) workAreaTypesToRemove.push(WorkAreaType.Yard)

    await equipmentService.builkAssignment({
      equipmentIds: formData.equipmentIds,
      operationalStatus: formData.status,
      workAreaTypesToRemove: workAreaTypesToRemove,
      assignments: assignments,
    })
  }

  public async fetchDataByIds(ids: number[]) {
    const missingIds = ids.filter(id => !this.items.some(item => item.id === id))
    if (!missingIds.length) return

    const equipments = await equipmentService.getOperationalEquipments(missingIds)

    this.updateStoreItems([...this.items, ...equipments])
  }

  private readonly handleLastJobUpsertedEvent = (res: IEvent<EquipmentLastJobUpsertedPayload>) => {
    if (!res.payload) return

    const item = this.items.find(x => x.id === res.payload?.equipmentId)

    if (!item) return

    const newItem = {
      ...item,
      lastJobOperatorName: res.payload?.lastJobUserName,
      lastJobDateTime: res.payload?.lastJobDateTime,
    }

    this.updateStoreItem(newItem, res.payload.equipmentId)
  }

  private readonly handleAssignmentUpsertedEvent = (
    res: IEvent<EquipmentAssignmentUpsertedPayload>,
  ) => {
    if (!res.payload) return

    const item = this.items.find(x => x.id === res.payload?.equipmentId)

    if (!item) return

    const newItem = {
      ...item,
      operationalStatus: res.payload?.operationalStatus,
      assignments: res.payload?.assignments,
    }

    this.updateStoreItem(newItem, res.payload.equipmentId)
  }

  private readonly handleEquipmentUpsertedEvent = (res: IEvent<EquipmentDto>) => {
    if (!res.payload) return

    const item = this.items.find(x => x.id === res.payload?.id)

    if (!item) return

    const newItem = {
      ...item,
      name: res.payload?.name,
    }

    this.updateStoreItem(newItem, res.payload.id)
  }
}
